import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { GoogleMap } from "@react-google-maps/api";
import RouteRenderer from "./RouteRenderer";
import EventMarkers from "./EventMarkers";
import "../../assets/scss/components/maps/VehicleRouteMap.scss";
import PropTypes from "prop-types";
import { useGoogleMaps } from '../../context/GoogleMapsContext';

const ROUTE_COLORS = [
  "#E53935", // Rojo
  "#1E88E5", // Azul
  "#43A047", // Verde
  "#FB8C00", // Naranja
  "#8E24AA", // Púrpura
  "#039BE5", // Azul claro
  "#FDD835", // Amarillo
  "#6D4C41", // Marron
  "#5E35B1", // Violeta
  "#00ACC1", // Cian
];

// Función auxiliar para crear un ID único para cada viaje
const getTripUniqueId = (trip) => {
  if (!trip) return "no-trip";
  return `${trip.vehiclePlate}-${trip.origin?.dateTime}-${trip.destination?.dateTime}`;
};

// Función para comparar si dos viajes son iguales
const isSameTrip = (trip1, trip2) => {
  if (!trip1 || !trip2) return false;
  return getTripUniqueId(trip1) === getTripUniqueId(trip2);
};

const VehicleRouteMap = ({
  allTrips,
  date,
  viewFilters,
  selectedTrip,
  selectedTrips = [],
  multipleRoutesEnabled = false,
  compactMode = false,
}) => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [filters, setFilters] = useState({
    showSpeedViolations: true,
    showCriticalPoints: true,
    showHarshAccel: false,
    showHarshBraking: false,
  });
  
  // Usar el contexto compartido en lugar de useJsApiLoader independiente
  const { isLoaded } = useGoogleMaps();
  
  // Mejorado para generar claves más estables
  const mapInstanceKey = useMemo(() => {
    // Componente específico de identificación
    const componentId = "vehicle-route-map";
    
    // Generar un ID único basado en datos relevantes
    const dataFingerprint = multipleRoutesEnabled 
      ? selectedTrips.map(trip => getTripUniqueId(trip)).join('-')
      : selectedTrip ? getTripUniqueId(selectedTrip) : 'no-trip';
    
    return `${componentId}-${dataFingerprint}`;
  }, [selectedTrip, selectedTrips, multipleRoutesEnabled]);
  
  const [renderKey, setRenderKey] = useState(0);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);
  
  const tripsToRender = useMemo(() => {
    if (multipleRoutesEnabled) {
      return selectedTrips;
    } else {
      return selectedTrip ? [selectedTrip] : [];
    }
  }, [multipleRoutesEnabled, selectedTrip, selectedTrips]);

  // Forzar renderizado cuando cambian las selecciones
  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [selectedTrip, selectedTrips, multipleRoutesEnabled]);
  
  const fitBounds = useCallback(() => {
    if (mapRef.current && tripsToRender.length > 0) {
      // Código existente para fitBounds...
      const bounds = new window.google.maps.LatLngBounds();
      let boundsExtended = false;

      tripsToRender.forEach((trip) => {
        if (!trip) return;

        // Añadir puntos de origen y destino
        if (trip.origin?.lat && trip.origin?.lon) {
          bounds.extend(
            new window.google.maps.LatLng(
              parseFloat(trip.origin.lat),
              parseFloat(trip.origin.lon)
            )
          );
          boundsExtended = true;
        }

        if (trip.destination?.lat && trip.destination?.lon) {
          bounds.extend(
            new window.google.maps.LatLng(
              parseFloat(trip.destination.lat),
              parseFloat(trip.destination.lon)
            )
          );
          boundsExtended = true;
        }

        // Si hay polyline de Google Maps
        if (trip.googleMapsPolyline) {
          try {
            const decodedPath = window.google.maps.geometry.encoding.decodePath(
              trip.googleMapsPolyline
            );
            decodedPath.forEach((point) => bounds.extend(point));
            boundsExtended = true;
          } catch (error) {
            console.error("Error decodificando polyline:", error);
          }
        }
      });

      // Verificar que bounds tiene puntos antes de aplicarlos
      if (boundsExtended && !bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds, { padding: 60 });
      } else {
        mapRef.current.setCenter({ lat: 2.7, lng: -74.0 });
        mapRef.current.setZoom(6);
      }
    }
  }, [tripsToRender]);

  // Aplicar filtros
  useEffect(() => {
    if (viewFilters) {
      setFilters(viewFilters);
    }
  }, [viewFilters]);

  // Ajustar mapa cuando cambia selectedTrip
  useEffect(() => {
    if (mapLoaded && tripsToRender.length > 0) {
      const timer = setTimeout(() => {
        if (mapRef.current) {
          window.google.maps.event.trigger(mapRef.current, "resize");
          fitBounds();
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [tripsToRender, mapLoaded, fitBounds, renderKey]);

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(mapRef.current);
        mapRef.current = null;
      }
    };
  }, []);

  const calculateCenter = () => {
    if (selectedTrip?.origin && selectedTrip?.destination) {
      return {
        lat:
          (parseFloat(selectedTrip.origin.lat) +
            parseFloat(selectedTrip.destination.lat)) /
          2,
        lng:
          (parseFloat(selectedTrip.origin.lon) +
            parseFloat(selectedTrip.destination.lon)) /
          2,
      };
    }
    return { lat: 2.7, lng: -74.0 };
  };

  if (!isLoaded) return <div className="map-loading">Cargando el mapa...</div>;

  return (
    <div className="vehicle-route-map-container compact-mode">
      <div className="google-map-container">
        {/* Usamos key para forzar que React destruya y recree el mapa completo */}
        <GoogleMap
          key={mapInstanceKey}
          mapContainerStyle={{
            width: "100%",
            height: compactMode ? "calc(100vh - 280px)" : "700px",
          }}
          center={calculateCenter()}
          zoom={9}
          options={{
            clickableIcons: false,
            streetViewControl: false,
            mapTypeId: "terrain",
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: 2,
              position: 3, // RIGHT_TOP
            },
            zoomControlOptions: {
              position: 9, // RIGHT_CENTER
            },
          }}
          onLoad={onMapLoad}
        >
          {/* Renderizar rutas solo cuando el mapa está cargado */}
          {mapLoaded && tripsToRender.map((trip, index) => (
            <React.Fragment key={`route-${getTripUniqueId(trip)}-${index}-${renderKey}`}>
              <RouteRenderer
                trip={trip}
                googleMapsPolyline={trip.googleMapsPolyline}
                actualPath={trip.records || []}
                routeColor={multipleRoutesEnabled ? ROUTE_COLORS[index % ROUTE_COLORS.length] : '#4285F4'}
                isSelected={isSameTrip(trip, selectedTrip)}
              />

              {isSameTrip(trip, selectedTrip) && (
                <EventMarkers trip={trip} filters={filters} />
              )}
            </React.Fragment>
          ))}
        </GoogleMap>
      </div>

      {selectedTrip && (
        <div className="trip-details">
          <div className="stats-grid">
            {/* Primera fila - Información de ruta */}
            <div className="stat-group route-info">
              <h4>Información de Ruta</h4>
              <div className="stat-row">
                <div className="stat-item">
                  <span>Ruta:</span>
                  <strong>{selectedTrip.routeName}</strong>
                </div>
                <div className="stat-item">
                  <span>Vehículo:</span>
                  <strong>
                    {selectedTrip.vehiclePlate || "No disponible"}
                  </strong>
                </div>
              </div>
            </div>

            {/* Segunda fila - Distancias */}
            <div className="stat-group distance-info">
              <h4>Distancias y Tiempos</h4>
              <div className="stat-row">
                <div className="stat-item">
                  <span>Distancia GPS:</span>
                  <strong>{selectedTrip.distanceTraveledKm} km</strong>
                </div>
                <div className="stat-item">
                  <span>Distancia Google Maps:</span>
                  <strong>
                    {selectedTrip.googleMapsDistanceKm || "N/A"} km
                  </strong>
                </div>
                <div className="stat-item">
                  <span>Diferencia:</span>
                  <strong>
                    {selectedTrip.googleMapsDistanceKm
                      ? Math.abs(
                          selectedTrip.distanceTraveledKm -
                            (selectedTrip.googleMapsDistanceKm || 0)
                        ).toFixed(2) + " km"
                      : "N/A"}
                  </strong>
                </div>
              </div>
              <div className="stat-row">
                <div className="stat-item">
                  <span>Tiempo Real:</span>
                  <strong>{selectedTrip.durationMinutes} min</strong>
                </div>
                <div className="stat-item">
                  <span>Tiempo Google Maps:</span>
                  <strong>
                    {selectedTrip.googleMapsDurationMinutes || "N/A"} min
                  </strong>
                </div>
                <div className="stat-item">
                  <span>Desviación Promedio:</span>
                  <strong>
                    {selectedTrip.deviationData?.avgDeviationMeters
                      ? `${Math.round(
                          selectedTrip.deviationData.avgDeviationMeters
                        )} m`
                      : "N/A"}
                  </strong>
                </div>
              </div>
            </div>

            {/* Tercera fila - Eventos y alertas */}
            <div className="stat-group events-info">
              <h4>Eventos Detectados</h4>
              <div className="stat-row">
                <div className="stat-item event-stat">
                  <span>Velocidad Máxima:</span>
                  <strong>{selectedTrip.maxSpeed || 0} km/h</strong>
                </div>
                <div className="stat-item event-stat">
                  <span>Excesos de Velocidad:</span>
                  <strong>{selectedTrip.overspeedCount || 0}</strong>
                </div>
                <div className="stat-item event-stat">
                  <span>Violaciones a Puntos Críticos:</span>
                  <strong>{selectedTrip.criticalViolationsCount || 0}</strong>
                </div>
              </div>
              <div className="stat-row">
                <div className="stat-item event-stat">
                  <span>Aceleraciones Bruscas:</span>
                  <strong>{selectedTrip.harshAccelerationCount || 0}</strong>
                </div>
                <div className="stat-item event-stat">
                  <span>Frenadas Bruscas:</span>
                  <strong>{selectedTrip.harshBrakingCount || 0}</strong>
                </div>
                <div className="stat-item event-stat">
                  <span>Eventos de Jamming:</span>
                  <strong>{selectedTrip.jammingCount || 0}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

VehicleRouteMap.propTypes = {
  allTrips: PropTypes.arrayOf(
    PropTypes.shape({
      vehiclePlate: PropTypes.string,
      routeName: PropTypes.string,
      origin: PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        dateTime: PropTypes.string
      }),
      destination: PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        dateTime: PropTypes.string
      }),
      distanceTraveledKm: PropTypes.number,
      durationMinutes: PropTypes.number,
      googleMapsPolyline: PropTypes.string,
      googleMapsDistanceKm: PropTypes.number,
      googleMapsDurationMinutes: PropTypes.number,
      maxSpeed: PropTypes.number,
      overspeedCount: PropTypes.number,
      criticalViolationsCount: PropTypes.number,
      harshAccelerationCount: PropTypes.number,
      harshBrakingCount: PropTypes.number,
      jammingCount: PropTypes.number,
      deviationData: PropTypes.shape({
        avgDeviationMeters: PropTypes.number
      }),
      records: PropTypes.array,
      overspeedEvents: PropTypes.array,
      criticalViolations: PropTypes.array,
      harshAccelerationEvents: PropTypes.array,
      harshBrakingEvents: PropTypes.array,
      jammingEvents: PropTypes.array
    })
  ),
  date: PropTypes.string,
  viewFilters: PropTypes.shape({
    showSpeedViolations: PropTypes.bool,
    showCriticalPoints: PropTypes.bool,
    showHarshAccel: PropTypes.bool,
    showHarshBraking: PropTypes.bool
  }),
  selectedTrip: PropTypes.object,
  selectedTrips: PropTypes.arrayOf(PropTypes.object),
  multipleRoutesEnabled: PropTypes.bool,
  compactMode: PropTypes.bool
};

export default VehicleRouteMap;